/**
 * Step 2 — Chatwork 側の移行対象を表示し、確認を取る
 *
 * 設計書: docs/chatwork_killer/03_product_strategy.md §3.2 (Wizard step 2)
 * Issue:   skill-bridge/media_app#6000
 *
 * 表示内容:
 *  - 大きな数字 4 つ (ルーム / メッセージ / タスク / ファイル)
 *  - ルーム一覧表 (チェックボックスで個別選択 — 既定全選択)
 *  - 「無償移行支援 ¥0」バナー
 *  - 「データ移行を開始」ボタン
 */

import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ListChecks,
  MessageSquare,
  FileText,
  Files,
  Loader2,
} from "lucide-react";
import type {
  ChatworkAccountInfo,
  MigrationPreview,
  PreviewRoom,
} from "./types";
import { MigrationApi, MigrationApiError } from "./api";

export interface PreviewStepProps {
  api: MigrationApi;
  jobId: string;
  account: ChatworkAccountInfo;
  /** 既存 preview があればスキップして再 fetch しない */
  existingPreview?: MigrationPreview | null;
  onConfirmed: (preview: MigrationPreview, selected_room_ids: number[] | null) => void | Promise<void>;
  onBack: () => void;
}

export function PreviewStep(props: PreviewStepProps) {
  const [preview, setPreview] = useState<MigrationPreview | null>(
    props.existingPreview ?? null,
  );
  const [loading, setLoading] = useState(!props.existingPreview);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    if (props.existingPreview) {
      setPreview(props.existingPreview);
      setSelected(
        new Set(props.existingPreview.rooms.map((r) => r.chatwork_room_id)),
      );
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    props.api
      .preview(props.jobId)
      .then((res) => {
        if (cancelled) return;
        setPreview(res.preview);
        setSelected(new Set(res.preview.rooms.map((r) => r.chatwork_room_id)));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof MigrationApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : String(e),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.api, props.jobId, props.existingPreview]);

  const toggle = (roomId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  };

  const allSelected = useMemo(
    () => preview != null && preview.rooms.every((r) => selected.has(r.chatwork_room_id)),
    [preview, selected],
  );

  const toggleAll = () => {
    if (!preview) return;
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(preview.rooms.map((r) => r.chatwork_room_id)));
    }
  };

  const onConfirm = async () => {
    if (!preview) return;
    if (selected.size === 0) {
      setError("少なくとも 1 つのルームを選択してください。");
      return;
    }
    setBusy(true);
    setError(null);
    const room_ids = preview.rooms.every((r) => selected.has(r.chatwork_room_id))
      ? null // null = all
      : Array.from(selected);
    try { await props.onConfirmed(preview, room_ids); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <section
      className="mx-auto w-full max-w-3xl rounded-lg border border-border-subtle bg-canvas p-8 shadow-md"
      aria-labelledby="preview-title"
      data-testid="preview-step"
    >
      <header className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          Step 2 / 4
        </p>
        <h2 id="preview-title" className="mt-1 text-2xl font-bold text-fg-primary">
          移行内容を確認
        </h2>
        <p className="mt-2 text-base text-fg-secondary">
          {props.account.name} ({props.account.email}) のアカウントから取得できる Chatwork データです。
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-success-bg px-3 py-1 text-sm font-medium text-success">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          無償移行支援 (¥0) — 移行は何度でもやり直せます
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          移行対象を集計しています...
        </div>
      ) : preview ? (
        <>
          <ul
            data-testid="preview-summary"
            className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4"
            aria-label="移行対象サマリ"
          >
            <Stat icon={<ListChecks className="h-5 w-5" />} label="ルーム" value={preview.rooms_total} />
            <Stat icon={<MessageSquare className="h-5 w-5" />} label="メッセージ" value={preview.messages_total} />
            <Stat icon={<FileText className="h-5 w-5" />} label="タスク" value={preview.tasks_total} />
            <Stat icon={<Files className="h-5 w-5" />} label="ファイル" value={preview.files_total} />
          </ul>

          <div className="rounded-md border border-border-subtle">
            <div className="flex items-center justify-between border-b border-border-subtle bg-surface-base px-4 py-2">
              <h3 className="text-sm font-medium text-fg-primary">
                ルーム一覧 ({preview.rooms.length} 件)
              </h3>
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-fg-secondary">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="select-all-rooms"
                />
                すべて選択
              </label>
            </div>
            <ul className="max-h-80 overflow-auto" role="list" data-testid="preview-rooms">
              {preview.rooms.map((r) => (
                <RoomRow
                  key={r.chatwork_room_id}
                  room={r}
                  checked={selected.has(r.chatwork_room_id)}
                  onToggle={() => toggle(r.chatwork_room_id)}
                />
              ))}
            </ul>
          </div>

          {error ? (
            <p role="alert" className="mt-3 text-sm text-danger" data-testid="preview-error">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={props.onBack}
              className="inline-flex h-11 items-center justify-center rounded-md border border-border-default bg-canvas px-5 font-medium text-fg-primary hover:bg-surface-base"
              data-testid="preview-back"
            >
              戻る
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || selected.size === 0}
              aria-busy={busy || undefined}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-brand-600 px-5 font-medium text-fg-inverse hover:bg-brand-700 disabled:bg-fg-subtle"
              data-testid="preview-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              データ移行を開始 ({selected.size} ルーム)
            </button>
          </div>
        </>
      ) : (
        <p role="alert" className="text-sm text-danger" data-testid="preview-error">
          {error ?? "プレビューを取得できませんでした。"}
        </p>
      )}
    </section>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <li className="rounded-md border border-border-subtle bg-surface-base p-4">
      <div className="flex items-center gap-2 text-fg-muted">
        {icon}
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-fg-primary">
        {value.toLocaleString("ja-JP")}
      </div>
    </li>
  );
}

function RoomRow({
  room,
  checked,
  onToggle,
}: {
  room: PreviewRoom;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <li className="flex items-center gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        aria-label={`${room.name} を移行対象にする`}
        data-testid={`preview-room-${room.chatwork_room_id}`}
      />
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium text-fg-primary">{room.name}</p>
        <p className="text-xs text-fg-muted">
          {room.type === "direct" ? "1:1" : room.type === "my" ? "マイチャット" : "グループ"}
        </p>
      </div>
      <div className="text-right text-xs text-fg-muted">
        <p>{room.message_count.toLocaleString("ja-JP")} msg</p>
        <p>
          {room.task_count} task / {room.file_count} file
        </p>
      </div>
    </li>
  );
}
